/**
 * @ngdoc service
 * @name runnerFlowsFormatter
 * @module flowingly.runner.services
 *
 * @description Service repsonsible for formatting flows
 */

import angular from 'angular';
import IFormattedFlow from '../interfaces/IFormattedFlow';
import IUserBasicInfo from '@Shared.Angular/@types/core/contracts/queryModel/user/UserBasicInfo';
import { SharedAngular } from '@Client/@types/sharedAngular';
import { IFlowForUser } from '@Shared.Angular/@types/core/contracts/queryModel/flows/flowForUser';
import IFormattedStep from '@Client/interfaces/IFormattedStep';
import IFormattedField from '@Client/interfaces/IFormattedField';
import IRunnerCard from '@Shared.Angular/@types/core/contracts/queryModel/card/runnerCard';
import IFieldForUser from '@Shared.Angular/@types/core/contracts/queryModel/flows/fieldForUser';
import { IGroupedFlowsForUser } from '@Shared.Angular/@types/core/contracts/queryModel/flows/groupedFlowsForUser';
import { IMapFlowForGetFlows } from '@Shared.Angular/@types/core/contracts/queryModel/flows/mapFlowForGetFlows';
import ActivityTaskType from '@Shared.Angular/@types/core/contracts/queryModel/workflow/steps/activityTaskType';
import IntegrationState from '@Shared.Angular/@types/core/contracts/queryModel/webhook/integrationState';
import { toCamelCaseDeep } from '@Shared.Angular/flowingly.services/case-converter';

const fieldTypes = {
  approvecomment: 0,
  approval: 1,
  checkbox: 3,
  currency: 4,
  fileupload: 6,
  instruction: 7,
  table: 8,
  textArea: 10,
  number: 12,
  radiobuttonlist: 14,
  dropdown: 15,
  tasklist: 17,
  multiselectlist: 18,
  signature: 21,
  dynamicactors: 22,
  lookup: 23,
  attachdocument: 25,
  image: 26,
  recaptcha: 27,
  procedure: 28,
  address: 29,
  externalbusiness: 31
};

export interface IFlowFormatOptions {
  replaceLoggedInUserWithYou: boolean;
}

const defaultFlowFormatOption: IFlowFormatOptions = {
  replaceLoggedInUserWithYou: true
};

export class FormattingTool {
  private user;
  private config: IFlowFormatOptions;

  public constructor(config: IFlowFormatOptions) {
    this.config = config;
  }

  public setCurrentUser(user) {
    this.user = user;
  }

  /**
   * @deprecated use .getNameToDisplay which is more reliable
   * @param taskActorName
   */
  public getActorName(taskActorName) {
    if (this.config.replaceLoggedInUserWithYou) {
      taskActorName = taskActorName && taskActorName.trim();

      //if the name is the same as the logged in user, return "you"
      if (this.user && this.user.fullName === taskActorName) {
        return 'You';
      } else {
        return taskActorName;
      }
    } else {
      return taskActorName;
    }
  }
}

export class RunnerFlowsFormatterService {
  static $inject = [
    'flowinglyConstants',
    'sessionService',
    'momentService',
    'lodashService',
    'flowsUtilityService',
    'validationService',
    'fileService',
    'flowinglyMomentService',
    'currencyService',
    'kendoService',
    'tokenService',
    'APP_CONFIG'
  ];

  constructor(
    private flowinglyConstants: SharedAngular.FlowinglyConstants,
    private sessionService: SharedAngular.SessionService,
    private momentService: Moment,
    private lodashService: Lodash,
    private flowsUtilityService: SharedAngular.FlowsUtilityService,
    private validationService: SharedAngular.ValidationService,
    private fileService: SharedAngular.FileService,
    private flowinglyMomentService: SharedAngular.FlowinglyMomentService,
    private currencyService: SharedAngular.CurrencyService,
    private kendo: Kendo,
    private tokenService: SharedAngular.TokenService,
    private appConfig: SharedAngular.APP_CONFIG
  ) {}

  private fileserviceInitiated = false;

  public getStartedByText(
    flow: IFormattedFlow,
    shouldIncludeTime = false
  ): string {
    const starter = flow.whoStarted;
    const requester = flow.requestedByUser
      ? this.getNameToDisplay(flow.requestedByUser)
      : null;

    let text: string;
    if (requester && requester != starter) {
      // if You != You
      const requesterName = this.getNameToDisplay(flow.requestedByUser);
      text = `Started by ${requesterName} on behalf of ${starter}`;
    } else {
      text = `Started by ${starter}`;
    }

    if (shouldIncludeTime) {
      text += ' on ' + flow.momentStartedFullDate;
    } else {
      text += ' ' + flow.momentStarted;
    }

    return text;
  }

  private getNameToDisplay(user: unknown): string {
    const currentUser = this.sessionService.getUser();
    if (!user) {
      return '';
    }

    const userDetails = user as IUserBasicInfo;

    const userEmail = userDetails.email || '';
    const firstName = userDetails.firstName || '';
    const lastName = userDetails.lastName || '';

    if (
      currentUser?.email &&
      userEmail &&
      currentUser.email.toLowerCase() === userEmail.toLowerCase()
    ) {
      return 'You';
    }

    return `${firstName} ${lastName}`.trim();
  }

  /**
   * Given an IFlowForUser, return an IFormattedFlowForUser copy of the flow.
   * @param _flow_
   * @param config
   * @param timeZone
   */
  public formatFlow(
    _flow_: IFlowForUser,
    timeZone: string = null,
    config: IFlowFormatOptions = defaultFlowFormatOption
  ) {
    const flow = angular.copy(_flow_) as IFormattedFlow;
    const _user = this.sessionService.getUser();
    flow.timeZone = timeZone;
    const formattingTool = new FormattingTool(config);
    formattingTool.setCurrentUser(_user);

    if (!this.fileserviceInitiated) {
      if (_user) {
        this.fileService.setUser(_user.id, this.tokenService.getTenant().id);
        this.fileserviceInitiated = true;
      }
    }

    flow.whoStarted = formattingTool.getActorName(flow.startedByName);
    flow.momentStarted = this.flowinglyMomentService.isOverOneDayAgo(
      flow.startedDate
    )
      ? ' on ' +
        this.flowinglyMomentService.formatFullDate(
          flow.startedDate,
          'LL',
          timeZone
        )
      : this.flowinglyMomentService.calculateRelativeTime(flow.startedDate);
    flow.momentStartedFullDate = this.flowinglyMomentService.formatFullDate(
      flow.startedDate,
      'LLLL',
      timeZone
    );
    flow.momentWaitingFor = this.flowinglyMomentService.isOverOneDayAgo(
      flow.waitingForDate
    )
      ? this.flowinglyMomentService.formatFullDate(
          flow.waitingForDate,
          'LL',
          timeZone
        )
      : this.flowinglyMomentService.calculateRelativeTime(flow.waitingForDate);
    flow.momentWaitingForLabel = this.flowinglyMomentService.isOverOneDayAgo(
      flow.waitingForDate
    )
      ? 'on '
      : ' ';
    flow.momentFinalised = this.flowinglyMomentService.calculateRelativeTime(
      flow.finalisedDate
    ); // was "momentCompleted"
    flow.momentFinalisedFullDate = this.flowinglyMomentService.formatFullDate(
      flow.finalisedDate,
      'LLLL',
      timeZone
    ); // was "momentCompleted"
    flow.momentFinalisedInLocalFormat =
      this.flowinglyMomentService.formatUTCToLocal(flow.finalisedDate);
    flow.momentDue = this.flowinglyMomentService.calculateDeadlineTime(
      flow.dueDate
    );
    flow.dueDateInLocalFormat = this.flowinglyMomentService.formatUTCToLocal(
      flow.dueDate
    );
    flow.finalisedInLocalFormat = this.flowinglyMomentService.formatUTCToLocal(
      flow.finalisedDate
    );
    flow.currentStepsHeader = flow.currentStepsHeader || '';
    flow.isOverdue = this.flowinglyMomentService.isOverdue(flow.dueDate);
    flow.isFinalised = flow.finalisedDate !== null;
    flow.ccUsers = this.getDisplayNames(flow.ccUsers);

    this.lodashService.forEach(flow.steps, (step) =>
      this.formatStep(flow, step, formattingTool, timeZone)
    );

    return flow;
  }

  public formatStep(
    flow: IFormattedFlow,
    step: IFormattedStep,
    formattingTool: FormattingTool,
    timeZone: string = null
  ) {
    this.lodashService.forEach(step.completedApprovals, (approval) => {
      (approval as any).relativeApprovalDate =
        this.flowinglyMomentService.calculateRelativeTime(
          (approval as any).approvalDate
        );
    });

    // This is doubling up with what we have in Steps.Fields but I had trouble giving Steps.Fields to FormGen
    // and so decided to sidestep the issue and reatain Step.CardTasks
    const cardTasks = step.cardTasks
      ? toCamelCaseDeep<IRunnerCard>(JSON.parse(step.cardTasks))
      : null;
    step.parsedCardTasks = cardTasks;
    step.schema = { fields: [] };

    if (cardTasks !== null && cardTasks !== undefined) {
      step.schema.fields = this.flowsUtilityService.toCamel(
        cardTasks.formElements
      );

      if (!this.appConfig.enableFormFieldReCaptcha) {
        step.schema.fields = step.schema.fields.filter(
          (f) => f.type != this.flowinglyConstants.formFieldType.RECAPTCHA
        );
      }

      if (!step.isCompleted) {
        if (flow.currentStepsHeader.length > 0)
          flow.currentStepsHeader = flow.currentStepsHeader + ', ' + step.name;
        else flow.currentStepsHeader = step.name;
      }
    }

    if (flow.isFinalised) {
      // We dont actually display this anywhere - but for completeness the
      // variable does show the most accurate info - in case it is needed
      if (flow.finalisedReason === 'Rejected') {
        flow.currentStepsHeader = 'Rejected';
      } else if (flow.finalisedReason === 'Withdrawn') {
        flow.currentStepsHeader = 'Cancelled';
      } else {
        flow.currentStepsHeader = flow.finalisedReason;
      }
    }

    step.whoCompletedStep = formattingTool.getActorName(step.completedByName);

    switch (step.stepType) {
      case ActivityTaskType.PARALLEL_APPROVAL: {
        let approverNamesArray = step.stepApproverNames.split(', ');
        approverNamesArray = approverNamesArray.map((nextApprover) => {
          return formattingTool.getActorName(nextApprover);
        });
        step.whoIsAssignedStep = formattingTool.getActorName(
          approverNamesArray.join(', ')
        );
        break;
      }
      case ActivityTaskType.SEQUENTIAL_APPROVAL:
        // FLOW-2752 Display all selected approvers in Runner
        if (step.stepApproverNames) {
          const approvers = step.stepApproverNames.split(', ');
          const user1 = approvers.shift();
          const userN = approvers.pop();
          const others = approvers;

          let str = formattingTool.getActorName(user1);

          if (others.length) {
            str +=
              ' then ' +
              others.map((u) => formattingTool.getActorName(u)).join(', ');
          }

          if (userN) {
            str += ' and ' + formattingTool.getActorName(userN);
          }

          step.whoIsAssignedStep = str;
        }
        break;
      default:
        step.whoIsAssignedStep = step.delegatorUserName
          ? `${formattingTool.getActorName(
              step.actorAssignedName
            )} on behalf of ${formattingTool.getActorName(
              step.delegatorUserName
            )}`
          : formattingTool.getActorName(step.actorAssignedName);
        break;
    }

    this.lodashService.forEach(
      step.stepReassignmentHistoryItems,
      (item: any) => {
        item.momentFromNowWithNoPrefix =
          this.flowinglyMomentService.calculateRelativeTimeWithNoPrefix(
            item.reassignmentDateTime
          );
        item.momentFromNow = this.flowinglyMomentService.calculateRelativeTime(
          item.reassignmentDateTime
        );
      }
    );

    if (step.completedDate) {
      step.moment =
        this.flowinglyMomentService.calculateRelativeTimeWithNoPrefix(
          step.completedDate
        );
      step.completedDateMoment =
        this.flowinglyMomentService.calculateRelativeTimeOrFullDate(
          step.completedDate
        );
    } else if (
      step.stepReassignmentHistoryItems &&
      step.stepReassignmentHistoryItems.length > 0
    ) {
      step.moment = (
        step.stepReassignmentHistoryItems[
          step.stepReassignmentHistoryItems.length - 1
        ] as any
      ).momentFromNowWithNoPrefix;
    } else {
      step.moment =
        this.flowinglyMomentService.calculateRelativeTimeWithNoPrefix(
          step.createdDate
        );
    }

    step.whoUpdatedStep = formattingTool.getActorName(step.updatedByName);
    step.deadLineLabel = 'None';
    if (step.dueDate !== null) {
      step.deadLineLabel = this.flowinglyMomentService.calculateDeadlineTime(
        step.dueDate
      );
      step.isOverdue = this.flowinglyMomentService.isOverdue(step.dueDate);
      step.momentFinalisedInLocalFormat =
        this.flowinglyMomentService.formatUTCToLocal(step.dueDate);
    }
    step.hasFields = false;
    this.lodashService.forEach(step.fields, (field) =>
      this.formatField(step, field as any)
    );

    if (step.stepTasks != null) {
      this.lodashService.forEach(step.stepTasks, (stepTask) => {
        this.formatStepTask(stepTask, formattingTool, timeZone);
      });
    }

    if (
      step.integrationState != null &&
      step.integrationState.state === IntegrationState.Processing
    ) {
      (step.integrationState as any).processingIntegrationMoment =
        this.flowinglyMomentService.calculateRelativeTimeWithNoPrefix(
          step.integrationState.createdDateUtc
        );
    }
  }

  // The original authors have done very-bad-things in this file, they modifiy and extend
  // objects like field instead of creating new objects,
  // so field:IFieldForUserPascalCase is passed in, and that same object is turned into field:IFormattedField
  // The original authors have done very-bad-things in this file, they modifiy and extend
  // objects like field instead of creating new objects,
  // so field:IFieldForUser is passed in, and that same object is turned into field:IFormattedField
  public formatField(step: IFormattedStep, _field: IFieldForUser) {
    const field = _field as unknown as Omit<IFormattedField, 'value'> & {
      value: string;
    };
    const fieldMetaData = step.schema.fields.find((x) => x.name == field.name);

    step.hasFields = true;
    if (
      field.type === fieldTypes.tasklist ||
      field.type === fieldTypes.multiselectlist
    ) {
      if (field.value === null) {
        field.value = ''; // For the current step
        field.text = ''; // For step history
      }
    }

    // weird ass pattern - Cassey
    switch (field.type) {
      case fieldTypes.approvecomment:
        field.type = 'approvecomment';
        break;
      case fieldTypes.approval:
        field.type = 'approval';
        break;
      case fieldTypes.checkbox:
        field.type = 'checkbox';
        break;
      case fieldTypes.currency:
        field.type = 'currency';
        break;
      case fieldTypes.fileupload:
        field.type = 'fileupload';
        break;
      case fieldTypes.instruction:
        field.type = 'instruction';
        break;
      case fieldTypes.table:
        field.type = 'table';
        break;
      case fieldTypes.textArea:
        field.type = 'textArea';
        break;
      case fieldTypes.number:
        field.type = 'number';
        break;
      case fieldTypes.radiobuttonlist:
        field.type = 'radiobuttonlist';
        break;
      case fieldTypes.dropdown:
        field.type = 'dropdown';
        break;
      case fieldTypes.tasklist:
        field.type = 'tasklist';
        break;
      case fieldTypes.multiselectlist:
        field.type = 'multiselectlist';
        break;
      case fieldTypes.signature:
        field.type = 'signature';
        break;
      case fieldTypes.dynamicactors:
        field.type = 'dynamicactors';
        break;
      case fieldTypes.lookup:
        field.type = 'lookup';
        break;
      case fieldTypes.attachdocument:
        field.type = 'attachdocument';
        break;
      case fieldTypes.image:
        field.type = 'image';
        break;
      case fieldTypes.recaptcha:
        field.type = this.flowinglyConstants.formFieldType.RECAPTCHA;
        break;
      case fieldTypes.procedure:
        field.type = this.flowinglyConstants.formFieldType.PROCEDURE;
        break;
      case fieldTypes.address:
        field.type = this.flowinglyConstants.formFieldType.ADDRESS;
        break;
      case fieldTypes.externalbusiness:
        field.type = this.flowinglyConstants.formFieldType.EXTERNAL_BUSINESS;
        break;
      default:
        field.type = 'string';
    }

    switch (field.type) {
      case 'table':
        this.lodashService.forEach(step.schema.fields, (schemaField) => {
          if (schemaField.name === field.name) {
            (field as any).tableSchema = schemaField.tableSchema;
            (schemaField as any).lookupValues = field.lookupValues;
          }
        });
        break;
      case 'fileupload':
        (field as any).hasFiles = false;
        if (
          (field.value && field.value !== '') ||
          (field.text && field.text !== '')
        ) {
          (field as any).hasFiles = true;
          const fileArray = [];
          const fileMetas = JSON.parse(field.text);
          if (fileMetas) {
            fileMetas.forEach((fileMeta) => {
              fileArray.push({
                filename: fileMeta.filename,
                downloadLink: this.fileService.getDownloadLink(fileMeta.id)
              });
            });
          }

          (field as any).text = fileArray;
        }
        break;
      case 'tasklist':
      case 'multiselectlist':
        if (field.value.length >= 1) {
          field.text = field.value = JSON.parse(field.value);
        }
        break;
      case 'currency':
        if (field.value) {
          const num = +field.value.split(' ').shift();
          const code = fieldMetaData.customUnits;

          field.text = this.currencyService.toCurrency(num, code);
        }
        break;
      case this.flowinglyConstants.formFieldType.ADDRESS:
        if (field.value) {
          try {
            const addressData = JSON.parse(field.value);
            field.text = JSON.stringify({
              street: addressData.street || '',
              suburb: addressData.suburb || '',
              town: addressData.town || '',
              region: addressData.region || '',
              postCode: addressData.postCode || '',
              country: addressData.country || ''
            });
          } catch (e) {
            console.error('Failed to parse address data:', e);
            field.text = field.value;
          }
        } else {
          field.text = '';
        }
        break;
      case this.flowinglyConstants.formFieldType.EXTERNAL_BUSINESS:
        if (field.value) {
          field.text = field.value;
          field.subFieldVisibility = fieldMetaData?.subFieldVisibility;
        } else {
          field.text = '';
        }
        break;
    }
  }

  public formatStepNudgeHistoryList(nudgeHistoryList, step) {
    for (const nudgeHistory of nudgeHistoryList) {
      this.formatStepNudgeHistory(nudgeHistory, step);
    }

    return nudgeHistoryList;
  }

  public formatStepNudgeHistory(nudgeHistory, step) {
    const config = {
        replaceLoggedInUserWithYou: true
      },
      _user = this.sessionService.getUser(),
      formattingTool = new FormattingTool(config);

    formattingTool.setCurrentUser(_user);

    nudgeHistory.whoNudged = formattingTool.getActorName(
      nudgeHistory.nudgedByUserName
    );
    nudgeHistory.momentNudged =
      this.flowinglyMomentService.calculateRelativeTime(
        nudgeHistory.createdDate
      );
    nudgeHistory.whoIsNudged = formattingTool.getActorName(
      nudgeHistory.actorNameNudged
    );
    nudgeHistory.stepName = step.name;
  }

  public encodeFlowFormData(formData, schema, isSaveProgress) {
    formData.forEach((data) => {
      if (angular.isString(data.value)) {
        const matchField = schema.find((f) => f.name === data.key);

        if (
          matchField &&
          matchField.type.toLowerCase() !==
            this.flowinglyConstants.formFieldType.TEXTAREA &&
          matchField.type.toLowerCase() !==
            this.flowinglyConstants.formFieldType.TABLE
        ) {
          if (
            isSaveProgress &&
            matchField.type.toLowerCase() ===
              this.flowinglyConstants.formFieldType.TEXT
          ) {
            data.value = this.validationService.sanitizeString(data.value);
          } else {
            data.value = this.kendo.htmlEncode(data.value);
          }
        }
      }
    });
  }

  private getDisplayNames(cc) {
    const listCC = [];

    if (cc && cc.length > 2) {
      const _json = toCamelCaseDeep(JSON.parse(cc));

      if (_json.users || _json.teams) {
        _json.users.forEach((elem, i) => {
          listCC.push(elem.name);
        });

        _json.teams.forEach((elem, i) => {
          if (elem.teamName === undefined) listCC.push(elem.name);
          else listCC.push(elem.teamName);
        });
      }
    }

    return listCC.toString().replace(',', ', ');
  }

  public formatStepTask(
    stepTask,
    formattingTool: FormattingTool = null,
    timeZone: string = null
  ) {
    const user = this.sessionService.getUser();

    if (formattingTool == null) {
      formattingTool = new FormattingTool(defaultFlowFormatOption);
    }

    formattingTool.setCurrentUser(user);

    // Due date properties.
    // When the task is rejected then the date to use is the UpdatedDateUtc.
    if (stepTask.approvalStatus != null && stepTask.approvalStatus === 1) {
      stepTask.moment =
        this.flowinglyMomentService.calculateRelativeTimeWithNoPrefix(
          stepTask.updatedDateUtc
        );
    } else {
      stepTask.moment =
        this.flowinglyMomentService.calculateRelativeTimeWithNoPrefix(
          stepTask.createdDateUtc
        );
    }

    stepTask.dueDateLabel = this.flowinglyMomentService.calculateDeadlineTime(
      stepTask.dueDateTime
    );
    stepTask.momentDueDateInLocalFormat =
      this.flowinglyMomentService.formatFullDate(
        stepTask.dueDateTime,
        'DD MMM YYYY',
        timeZone
      );

    if (stepTask.status === 0 || stepTask.status === 2) {
      // In progress or completed - with assigned user.
      stepTask.whoIsAssignedStepTask = formattingTool.getActorName(
        stepTask.assignedUserName
      );
    } else if (stepTask.status === 1) {
      // Waiting for approval - with approver user.
      stepTask.approvalMoment =
        this.flowinglyMomentService.calculateRelativeTimeWithNoPrefix(
          stepTask.updatedDateUtc
        );
      stepTask.approvalDateLabel =
        this.flowinglyMomentService.calculateDeadlineTime(
          stepTask.updatedDateUtc
        );
      stepTask.momentApprovalDateFormat =
        this.flowinglyMomentService.formatFullDate(
          stepTask.updatedDateUtc,
          'DD MMM YYYY',
          timeZone
        );

      stepTask.whoIsAssignedStepTask = formattingTool.getActorName(
        stepTask.approverUserName
      );
    }

    if (stepTask.status === 2) {
      // The step task is completed so there should be a completed date.
      stepTask.completedMoment =
        this.flowinglyMomentService.calculateRelativeTimeWithNoPrefix(
          stepTask.completedDateUtc
        );
      stepTask.completedDateLabel =
        this.flowinglyMomentService.calculateStepTaskCompletedTime(
          stepTask.completedDateUtc
        );
      stepTask.momentCompletedDateFormat =
        this.flowinglyMomentService.formatFullDate(
          stepTask.completedDateUtc,
          'DD MMM YYYY',
          timeZone
        );
    }

    stepTask.doneMoment =
      this.flowinglyMomentService.calculateRelativeTimeWithNoPrefix(
        stepTask.doneDateUtc
      );
  }

  /*
   * Reorder step tasks by ordering by due date time and then grouped by current user.
   * Current user step tasks are to be displayed first then step tasks of other users.
   */
  public reorderStepTasks(stepTasks) {
    if (stepTasks != null && stepTasks.length !== 0) {
      const currentUserId = this.sessionService.getUser().id;

      // Sort the step tasks by the DueDateTime.
      const orderedStepTaskByDueDate = this.lodashService.sortBy(
        stepTasks,
        ['dueDateTime'],
        ['asc']
      );

      const tasksByCurrentUserInComplete = [];
      // Get the incomplete step tasks where the current user is either the approver or assignee.
      this.lodashService.forEach(orderedStepTaskByDueDate, (stepTask) => {
        if (
          stepTask.assignedUserId === currentUserId &&
          stepTask.status === this.flowinglyConstants.stepTaskStatus.InProgress
        ) {
          tasksByCurrentUserInComplete.push(stepTask);
        }
        if (
          stepTask.approverUserId === currentUserId &&
          stepTask.status ===
            this.flowinglyConstants.stepTaskStatus.WaitingForApproval
        ) {
          tasksByCurrentUserInComplete.push(stepTask);
        }
      });

      const tasksByCurrentUserComplete = [];
      // Get all step tasks that are completed that current users is approver or assignee.
      this.lodashService.forEach(orderedStepTaskByDueDate, (stepTask) => {
        if (
          stepTask.status ===
            this.flowinglyConstants.stepTaskStatus.Completed &&
          stepTask.assignedUserId === currentUserId
        ) {
          tasksByCurrentUserComplete.push(stepTask);
        }
      });

      // Combine the current user step tasks that are not completed then completed and the remaining
      // with the other step tasks by removing duplicates.
      const sortedAndOrderedArray = this.lodashService.union(
        tasksByCurrentUserInComplete,
        tasksByCurrentUserComplete,
        orderedStepTaskByDueDate
      );

      return sortedAndOrderedArray;
    }
    return stepTasks;
  }

  public setGroupedFlowDisplayProperties(groups: IGroupedFlowsForUser[]) {
    const user = this.sessionService.getUser();
    return groups.map((group) => {
      return {
        ...group,
        show: true,
        flows: group.flows.map((flow) => {
          return {
            ...flow,
            isOverdue: this.flowinglyMomentService.isOverdue(flow.dueDate),
            momentDue: this.flowinglyMomentService.calculateDeadlineTime(
              flow.dueDate
            ),
            whoStarted:
              flow.startedByName === user.fullName ? 'You' : flow.startedByName,
            momentStarted:
              this.flowinglyMomentService.calculateRelativeTimeOrFullDate(
                flow.startedDate,
                'LL'
              ),
            dueDateInLocalFormat: this.flowinglyMomentService.formatUTCToLocal(
              flow.dueDate
            ),
            isFinalised: !!flow.finalisedDate,
            momentFinalised: this.flowinglyMomentService.calculateRelativeTime(
              flow.finalisedDate
            ),
            momentFinalisedInLocalFormat:
              this.flowinglyMomentService.formatUTCToLocal(flow.finalisedDate),
            show: true
          } as IFlowForDisplay;
        })
      } as IGroupedFlowsForDisplay;
    });
  }
}

angular
  .module('flowingly.runner.services')
  .service('runnerFlowsFormatter', RunnerFlowsFormatterService);

export type RunnerFlowsFormatterServiceType = InstanceType<
  typeof RunnerFlowsFormatterService
>;

export interface IGroupedFlowsForDisplay extends IGroupedFlowsForUser {
  show: boolean;
  flows: IFlowForDisplay[];
}

export interface IFlowForDisplay extends IMapFlowForGetFlows {
  isOverdue: boolean;
  momentDue: string;
  whoStarted: string;
  momentStarted: string;
  dueDateInLocalFormat: string;
  isFinalised: boolean;
  momentFinalised: string;
  momentFinalisedInLocalFormat: string;
  show: boolean;
}
